import { z } from "zod";

// ============================================================================
// Data Stream Key Constants
// ============================================================================

/**
 * Keys for AI UI stream data parts.
 *
 * These are the suffixes used in `data-${key}` stream parts.
 * Add new keys here to extend the data stream protocol.
 *
 * @example
 * ```typescript
 * // Stream part type: 'data-stream-id'
 * channel.writeData(AI_UI_STREAM_DATA_KEYS.STREAM_ID, { streamId: '123' })
 * ```
 */
export const AI_UI_STREAM_DATA_KEYS = {
	STREAM_ID: "stream-id",
	USAGE: "usage",
	SOURCE: "source",
} as const;

/**
 * Full data part type names (prefixed with 'data-').
 *
 * Use these for type checking received stream parts.
 *
 * @example
 * ```typescript
 * if (part.type === AI_UI_STREAM_DATA_PARTS.STREAM_ID) {
 *   const { streamId } = part.data
 * }
 * ```
 */
export const AI_UI_STREAM_DATA_PARTS = {
	STREAM_ID: `data-${AI_UI_STREAM_DATA_KEYS.STREAM_ID}`,
	USAGE: `data-${AI_UI_STREAM_DATA_KEYS.USAGE}`,
	SOURCE: `data-${AI_UI_STREAM_DATA_KEYS.SOURCE}`,
} as const;

// ============================================================================
// Data Part Payload Types
// ============================================================================

/**
 * Stream ID data payload.
 *
 * Sent as the first data part to identify the active stream.
 * Used for stream resumption and tracking.
 */
export interface AiUiStreamIdData {
	streamId: string;
}

/**
 * Map of data keys to their payload types.
 *
 * This is the core type that defines what data can be streamed.
 * Extend this interface to add new data types.
 *
 * @example
 * ```typescript
 * // In shared-types (extend the base types)
 * export type CustomDataTypes = AiUiStreamDataTypes & {
 *   textDelta: string
 *   suggestion: Suggestion
 * }
 * ```
 */
/**
 * A citation source emitted during AI streaming.
 * Carries enough metadata for the frontend to render inline citations.
 */
export const AiUiStreamSourceDataSchema = z.object({
	filename: z.string().optional(),
	pageNumber: z.number().int().positive().optional(),
	providerMetadata: z.record(z.string(), z.unknown()).optional(),
	rowRange: z.string().optional(),
	sheetName: z.string().optional(),
	sourceId: z.string().min(1),
	title: z.string().min(1),
});

export type AiUiStreamSourceData = z.infer<typeof AiUiStreamSourceDataSchema>;

export interface AiUiStreamDataTypes {
	[AI_UI_STREAM_DATA_KEYS.STREAM_ID]: AiUiStreamIdData;
	[AI_UI_STREAM_DATA_KEYS.USAGE]: AiUsageCostData;
	[AI_UI_STREAM_DATA_KEYS.SOURCE]: AiUiStreamSourceData;
	[key: string]: unknown;
}

// ============================================================================
// Transient Data Part Support (Reference: ai-chatbot pattern)
// ============================================================================

/**
 * Options for creating data parts.
 *
 * The `transient` flag indicates that the data part should NOT be persisted
 * in message history. Use this for streaming deltas that are only relevant
 * during active streaming (e.g., textDelta, codeDelta).
 *
 * Reference: Vercel AI Chatbot uses `transient: true` for all artifact streaming
 * deltas (data-textDelta, data-codeDelta, etc.) to prevent polluting message history.
 *
 * @example
 * ```typescript
 * // Transient: streaming delta (not persisted)
 * dataStream.write({
 *   type: 'data-textDelta',
 *   data: 'Hello',
 *   transient: true,
 * })
 *
 * // Persistent: final state (persisted in message history)
 * dataStream.write({
 *   type: 'data-usage',
 *   data: { inputTokens: 100, outputTokens: 50 },
 * })
 * ```
 */
export interface DataPartOptions {
	/**
	 * When true, this data part is not persisted in message history.
	 * Use for streaming deltas that aggregate into final content.
	 *
	 * Default: false (data part is persisted)
	 */
	transient?: boolean;
}

/**
 * Create a typed data part with optional transient flag.
 *
 * Use this for the base `AiUiStreamDataTypes` (stream-id, usage, source).
 * Artifact-specific data parts are created with `createCustomDataPart` from
 * `./chat`.
 *
 * @param key - The data key (without 'data-' prefix)
 * @param data - The payload matching the key's type
 * @param options - Optional settings like transient flag
 *
 * @example
 * ```typescript
 * const usage = createBaseDataPart('usage', { inputTokens: 100 })
 * ```
 */
export function createBaseDataPart<K extends keyof AiUiStreamDataTypes>(
	key: K,
	data: AiUiStreamDataTypes[K],
	options?: DataPartOptions
): {
	type: `data-${K & string}`;
	data: AiUiStreamDataTypes[K];
	transient?: boolean;
} {
	return {
		type: `data-${key as string}` as `data-${K & string}`,
		data,
		...(options?.transient === true ? { transient: true } : {}),
	};
}

export interface AiUsageCostUSD {
	cacheReadUSD?: number;
	inputUSD?: number;
	outputUSD?: number;
	reasoningUSD?: number;
	totalUSD?: number;
}

export interface AiUsageCostContext {
	combinedMax?: number;
	inputMax?: number;
	outputMax?: number;
	totalMax?: number;
}

export interface AiOutputChunkTimingStats {
	avg?: number;
	max?: number;
	median?: number;
	min?: number;
	p10?: number;
	p90?: number;
}

export interface AiGenerationPerformanceData {
	effectiveOutputTokensPerSecond?: number;
	effectiveTotalTokensPerSecond?: number;
	inputTokensPerSecond?: number;
	outputTokensPerSecond?: number;
	responseTimeMs?: number;
	stepTimeMs?: number;
	timeBetweenOutputChunksMs?: AiOutputChunkTimingStats;
	timeToFirstOutputMs?: number;
	toolExecutionMaxMs?: number;
	toolExecutionMs?: Record<string, number>;
	toolExecutionTotalMs?: number;
}

export interface AiGenerationStepData {
	finishReason?: string;
	inputTokens?: number;
	modelId?: string;
	outputTokens?: number;
	performance?: AiGenerationPerformanceData;
	provider?: string;
	rawFinishReason?: string;
	stepNumber: number;
	toolCallCount?: number;
	toolErrorCount?: number;
	toolNames?: string[];
	toolResultCount?: number;
	totalTokens?: number;
}

/** Multi-agent RAG answering mode a reply ran under (1/4/5). */
export type AiAgentMode = "1" | "4" | "5";

/**
 * Per-agent metrics for a multi-agent (mode 4/5) generation — one entry per
 * role that ran. Tokens/cost sum across the pipeline; `latencyMs` is that
 * agent's own wall time; `loops` is the verifier's iteration count.
 */
export interface AiAgentRoleMetric {
	calls?: number;
	costUsd?: number;
	inputTokens?: number;
	latencyMs?: number;
	loops?: number;
	model: string;
	outputTokens?: number;
	role: "main" | "researcher" | "verifier";
	toolCalls?: number;
	totalTokens?: number;
}

/** One billed audio-to-text fallback that ran before the chat model. */
export interface AiAudioTranscriptionMetric {
	costSource: "estimated";
	costUsd: number;
	durationSeconds: number;
	latencyMs: number;
	model: string;
	provider: string;
}

/**
 * Shared usage payload for AI SDK v7 UI message stream data parts.
 * This is emitted as `data-usage` from the backend and consumed by the frontend UI.
 */
export interface AiUsageCostData {
	/** Per-agent breakdown for modes 4/5. Absent on mode 1. */
	agents?: AiAgentRoleMetric[];
	/** Audio converted to text because the selected model lacked native audio input. */
	audioTranscriptions?: AiAudioTranscriptionMetric[];
	cachedInputTokens?: number;
	cacheWriteInputTokens?: number;
	context?: AiUsageCostContext;
	costUSD?: AiUsageCostUSD;
	finishReason?: string | null;
	hasPerformance?: boolean;
	hasUsage?: boolean;
	inputTokens?: number;
	/** Multi-agent answering mode (modes 4/5). Absent on the single-agent path. */
	mode?: AiAgentMode;
	modelId?: string;
	noCacheInputTokens?: number;
	outputTokens?: number;
	performance?: AiGenerationPerformanceData;
	provider?: string;
	reasoningTokens?: number;
	status?: "success" | "error" | "aborted";
	stepCount?: number;
	steps?: AiGenerationStepData[];
	/**
	 * Draft generations cancelled because the customer kept typing before the
	 * reply finished. Their spend is folded into `costUSD.totalUSD`; itemized
	 * here so the metrics chip can explain the blended total.
	 */
	superseded?: AiSupersededGenerations;
	textOutputTokens?: number;
	toolCallCount?: number;
	toolErrorCount?: number;
	toolResultCount?: number;
	totalTokens?: number;
}

/** Cancelled-draft accounting carried on the reply that superseded them. */
export interface AiSupersededGenerations {
	costUsd?: number;
	count: number;
	inputTokens?: number;
	outputTokens?: number;
	totalTokens?: number;
}

export const AiUiStreamIdDataSchema = z.object({
	streamId: z.string().min(1),
});

export const AiUsageCostUSDSchema = z.object({
	inputUSD: z.number().optional(),
	outputUSD: z.number().optional(),
	cacheReadUSD: z.number().optional(),
	reasoningUSD: z.number().optional(),
	totalUSD: z.number().optional(),
});

export const AiUsageCostContextSchema = z.object({
	inputMax: z.number().optional(),
	outputMax: z.number().optional(),
	totalMax: z.number().optional(),
	combinedMax: z.number().optional(),
});

export const AiOutputChunkTimingStatsSchema = z.object({
	min: z.number().optional(),
	p10: z.number().optional(),
	median: z.number().optional(),
	avg: z.number().optional(),
	p90: z.number().optional(),
	max: z.number().optional(),
});

export const AiGenerationPerformanceDataSchema = z.object({
	effectiveOutputTokensPerSecond: z.number().optional(),
	outputTokensPerSecond: z.number().optional(),
	inputTokensPerSecond: z.number().optional(),
	effectiveTotalTokensPerSecond: z.number().optional(),
	stepTimeMs: z.number().optional(),
	responseTimeMs: z.number().optional(),
	toolExecutionMs: z.record(z.string(), z.number()).optional(),
	toolExecutionTotalMs: z.number().optional(),
	toolExecutionMaxMs: z.number().optional(),
	timeToFirstOutputMs: z.number().optional(),
	timeBetweenOutputChunksMs: AiOutputChunkTimingStatsSchema.optional(),
});

export const AiGenerationStepDataSchema = z.object({
	stepNumber: z.number().int(),
	provider: z.string().optional(),
	modelId: z.string().optional(),
	finishReason: z.string().optional(),
	rawFinishReason: z.string().optional(),
	inputTokens: z.number().int().optional(),
	outputTokens: z.number().int().optional(),
	totalTokens: z.number().int().optional(),
	toolCallCount: z.number().int().optional(),
	toolResultCount: z.number().int().optional(),
	toolErrorCount: z.number().int().optional(),
	toolNames: z.array(z.string()).optional(),
	performance: AiGenerationPerformanceDataSchema.optional(),
});

export const AiAgentRoleMetricSchema = z.object({
	role: z.enum(["main", "researcher", "verifier"]),
	model: z.string(),
	calls: z.number().int().nonnegative().optional(),
	toolCalls: z.number().int().nonnegative().optional(),
	inputTokens: z.number().int().nonnegative().optional(),
	outputTokens: z.number().int().nonnegative().optional(),
	totalTokens: z.number().int().nonnegative().optional(),
	costUsd: z.number().nonnegative().optional(),
	latencyMs: z.number().nonnegative().optional(),
	loops: z.number().int().nonnegative().optional(),
});

export const AiAudioTranscriptionMetricSchema = z.object({
	provider: z.string(),
	model: z.string(),
	durationSeconds: z.number().nonnegative(),
	costUsd: z.number().nonnegative(),
	latencyMs: z.number().nonnegative(),
	costSource: z.literal("estimated"),
});

export const AiUsageCostDataSchema = z.object({
	audioTranscriptions: z
		.array(AiAudioTranscriptionMetricSchema)
		.max(8)
		.optional(),
	inputTokens: z.number().int().optional(),
	outputTokens: z.number().int().optional(),
	totalTokens: z.number().int().optional(),
	cachedInputTokens: z.number().int().optional(),
	cacheWriteInputTokens: z.number().int().optional(),
	noCacheInputTokens: z.number().int().optional(),
	textOutputTokens: z.number().int().optional(),
	reasoningTokens: z.number().int().optional(),
	modelId: z.string().optional(),
	provider: z.string().optional(),
	finishReason: z.string().nullable().optional(),
	status: z.enum(["success", "error", "aborted"]).optional(),
	stepCount: z.number().int().optional(),
	toolCallCount: z.number().int().optional(),
	toolResultCount: z.number().int().optional(),
	toolErrorCount: z.number().int().optional(),
	hasUsage: z.boolean().optional(),
	hasPerformance: z.boolean().optional(),
	performance: AiGenerationPerformanceDataSchema.optional(),
	steps: z.array(AiGenerationStepDataSchema).optional(),
	costUSD: AiUsageCostUSDSchema.optional(),
	context: AiUsageCostContextSchema.optional(),
	mode: z.enum(["1", "4", "5"]).optional(),
	agents: z.array(AiAgentRoleMetricSchema).max(8).optional(),
	/**
	 * Draft generations cancelled because the customer kept typing before the
	 * reply finished. Their spend is folded into `costUSD.totalUSD`; this block
	 * itemizes it so the metrics chip can explain the blended total.
	 */
	superseded: z
		.object({
			count: z.number().int().nonnegative(),
			inputTokens: z.number().int().nonnegative().optional(),
			outputTokens: z.number().int().nonnegative().optional(),
			totalTokens: z.number().int().nonnegative().optional(),
			costUsd: z.number().nonnegative().optional(),
		})
		.optional(),
});
